'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {X, CheckCircle, XCircle} from 'lucide-react';
import {cn} from '@/matic/lib/utils';

// Toast variants using CVA
const toastVariants = cva(
  'relative flex w-full items-center space-x-3 rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        success: 'bg-green-50 border-green-200 text-green-800',
        error: 'bg-red-50 border-red-200 text-red-800',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        info: 'bg-blue-50 border-blue-200 text-blue-800',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const iconVariants = cva('h-5 w-5 flex-shrink-0', {
  variants: {
    variant: {
      success: 'text-green-600',
      error: 'text-red-600',
      warning: 'text-yellow-600',
      info: 'text-blue-600',
    },
  },
});

// Toast types
type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ToastProps = {
  id: string;
  title: string;
  description?: string;
  duration?: number;
  action?: ToastAction;
  onDismiss: (id: string) => void;
} & VariantProps<typeof toastVariants>;

export type ToastOptions = {
  title: string;
  description?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: ToastAction;
};

type ToastContextType = {
  toast: (options: ToastOptions) => void;
};

// Toast context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Toast component
export function Toast({
  id,
  title,
  description,
  variant,
  action,
  onDismiss,
}: ToastProps) {
  const getIcon = () => {
    switch (variant) {
      case 'success': {
        return <CheckCircle className={cn(iconVariants({variant}))} />;
      }

      case 'error': {
        return <XCircle className={cn(iconVariants({variant}))} />;
      }

      case 'warning':
      case 'info':
      case undefined:
      case null: {
        return null;
      }
    }
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(toastVariants({variant}))}
    >
      {getIcon()}
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="mt-1 text-sm opacity-90">{description}</p>
        )}
      </div>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            onDismiss(id);
          }}
          className="flex-shrink-0 rounded-md border border-current px-2 py-1 text-xs font-medium hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          onDismiss(id);
        }}
        className="flex-shrink-0 rounded-md p-1 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Toast provider component
export function ToastProvider({children}: {children: React.ReactNode}) {
  const [toasts, setToasts] = useState<Array<ToastOptions & {id: string}>>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((previous) => previous.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (options: ToastOptions) => {
      const id = Math.random().toString(36).slice(2, 11);
      const newToast = {...options, id};

      setToasts((previous) => [...previous, newToast]);

      // Auto-dismiss after duration (default 5 seconds)
      const duration = options.duration ?? 5000;
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  const contextValue: ToastContextType = {
    toast: addToast,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast container */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col space-y-2"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            description={toast.description}
            variant={toast.variant}
            action={toast.action}
            onDismiss={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// UseToast hook
export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
